import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { ClinicalObservation } from "../normalized-results";

const PAGE_SIZE = 100;

/**
 * Read the normalized observation resource. The legacy test-result endpoint
 * remains available through useTestResults while migrated observations are
 * rolled out, so callers can choose the canonical fallback hook below.
 */
export function useClinicalObservations(opts?: { patientId?: string | null }) {
  const { client, patientId: currentPatientId } = useApiClient();
  const patientId = opts?.patientId ?? currentPatientId;

  return useQuery<ClinicalObservation[]>({
    queryKey: ["clinical-observations", patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error(
          "Cannot load clinical observations: missing patientId",
        );
      }

      const observations: ClinicalObservation[] = [];
      let cursor: string | undefined;

      while (true) {
        const { data, error } = await client.GET(
          "/patients/{patientId}/clinical-observations",
          {
            params: {
              path: { patientId },
              query: cursor
                ? { limit: PAGE_SIZE, cursor }
                : { limit: PAGE_SIZE },
            },
          },
        );

        if (!data) {
          throwApiError(
            error,
            "Failed to load clinical observations",
            "clinical-observations",
          );
        }

        observations.push(...data.data.items);
        if (!data.data.nextCursor) return observations;
        cursor = data.data.nextCursor;
      }
    },
    enabled: Boolean(patientId),
  });
}
